import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { vehicleOpsApi } from "@/services/vehicleOps";
import { listVehicles } from "@/services/vehicles";

const STEPS = ["选车 + 故障", "维修类型", "维修厂", "工时 + 配件", "确认提交"];

const TYPES = [
  { value: "scheduled", icon: "🔧", label: "保养（定期）", sub: "换油 / 滤清器 / 检查 · 标准化" },
  { value: "breakdown", icon: "🔥", label: "故障", sub: "功能异常 · 需修" },
  { value: "breakdown_major", icon: "🚧", label: "大修", sub: "总成更换 · > 8h 工时", enum: "breakdown" },
  { value: "accident", icon: "💥", label: "事故", sub: "碰撞 / 划伤 · 涉及保险" },
];
const URGENCY = [
  { value: "low", icon: "🟢", label: "不紧急", sub: "排进下次保养窗口" },
  { value: "normal", icon: "🟡", label: "一般紧急", sub: "本周内安排" },
  { value: "high", icon: "🔴", label: "紧急", sub: "24h 内 · 自动 hold 该车" },
];

const cents = (s: string) => Math.round(Number(s || 0) * 100);

export function VehicleOpsMaintenanceForm() {
  const nav = useNavigate();
  const vehQ = useQuery({ queryKey: ["vehicles", "all"], queryFn: () => listVehicles() });
  const [step, setStep] = useState(1);

  const [f, setF] = useState({
    vehicleId: "", reporter: "", reportedAt: new Date().toISOString().slice(0, 16),
    faultDesc: "", obdCode: "",
    typeCard: "scheduled", urgency: "normal",
    shopName: "", shopAddress: "", shopPhone: "", shopContact: "", warranty: "", returning: false,
    laborHours: "", laborRate: "", parts: "", totalCost: "",
  });
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const laborTotal = Math.round(Number(f.laborHours || 0) * Number(f.laborRate || 0) * 100);
  const typeMeta = TYPES.find((t) => t.value === f.typeCard);
  const veh = (vehQ.data ?? []).find((v) => v.id === f.vehicleId);

  function next() {
    setError(null);
    if (step === 1 && !f.vehicleId) { setError("请选择车辆"); return; }
    if (step === 1 && !f.faultDesc.trim()) { setError("请填写故障描述"); return; }
    if (step === 3 && !f.shopName.trim()) { setError("请填写维修厂名"); return; }
    setStep((s) => Math.min(5, s + 1));
  }

  async function submit() {
    setError(null);
    const total = f.totalCost ? cents(f.totalCost) : laborTotal;
    const desc = [
      `[${typeMeta?.label}] ${f.faultDesc}`,
      f.obdCode ? `OBD: ${f.obdCode}` : "",
      f.reporter ? `报修人: ${f.reporter}` : "",
      `紧急度: ${URGENCY.find((u) => u.value === f.urgency)?.label}`,
      f.shopAddress ? `厂址: ${f.shopAddress}` : "",
      f.shopPhone ? `电话: ${f.shopPhone}` : "",
      f.shopContact ? `联系人: ${f.shopContact}` : "",
      f.laborHours ? `工时: ${f.laborHours}h × $${f.laborRate}` : "",
      f.parts ? `配件: ${f.parts}` : "",
    ].filter(Boolean).join(" · ");
    setSubmitting(true);
    try {
      await vehicleOpsApi.createMaintenance({
        vehicle_id: f.vehicleId,
        maintenance_type: typeMeta?.enum ?? f.typeCard,
        description: desc,
        estimated_cost_cents: total || undefined,
        scheduled_at: f.reportedAt ? new Date(f.reportedAt).toISOString() : undefined,
        vendor_name: f.shopName || undefined,
      });
      nav("/admin/vehicle-operations/maintenance");
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-50">
      {/* 顶栏 */}
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3.5">
        <div>
          <h1 className="text-[17px] font-bold text-slate-900">🔧 新建维修工单</h1>
          <p className="text-[12px] text-slate-500">5 步向导 · 维修厂手工输入 · 提交后走老板审批</p>
        </div>
        <button onClick={() => nav("/admin/vehicle-operations/maintenance")} className="rounded-lg border border-slate-300 px-3.5 py-2 text-[13px] font-medium hover:bg-slate-50">✕ 取消</button>
      </header>

      {/* 步骤指示 */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white px-6 py-3">
        {STEPS.map((s, i) => {
          const n = i + 1;
          return (
            <div key={s} className="flex items-center gap-1">
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${n < step ? "bg-emerald-500 text-white" : n === step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>{n < step ? "✓" : n}</div>
              <span className={`whitespace-nowrap text-[12px] ${n === step ? "font-semibold text-slate-900" : "text-slate-400"}`}>{s}</span>
              {n < 5 && <span className="mx-1 text-slate-300">—</span>}
            </div>
          );
        })}
      </div>

      {/* 内容（滚动·铺满宽度）*/}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="rounded-xl border border-slate-200 bg-white p-8">
        {/* Step 1 */}
        {step === 1 && (
          <div className="flex flex-col gap-4">
            <H>Step 1 · 选车 + 故障描述</H>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(vehQ.data ?? []).map((v) => (
                <button key={v.id} onClick={() => set("vehicleId", v.id)} className={`rounded-lg border-2 p-3 text-left ${f.vehicleId === v.id ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                  <div className="text-[13px] font-semibold text-slate-900">{v.vehicle_number} · {v.model_label_cn}</div>
                  <div className="text-[11px] text-slate-500">{v.mileage_km != null ? `${v.mileage_km.toLocaleString()} km · ` : ""}{v.status === "active" ? "在役" : v.status}</div>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="报修人 *"><input value={f.reporter} onChange={(e) => set("reporter", e.target.value)} placeholder="如 司导张三" className={inp} /></Field>
              <Field label="报修时间 *"><input type="datetime-local" value={f.reportedAt} onChange={(e) => set("reportedAt", e.target.value)} className={inp} /></Field>
            </div>
            <Field label="故障描述 *"><textarea value={f.faultDesc} onChange={(e) => set("faultDesc", e.target.value)} rows={3} placeholder="如 换挡顿挫严重" className={inp} /></Field>
            <Field label="故障代码（OBD-II · 选填）"><input value={f.obdCode} onChange={(e) => set("obdCode", e.target.value)} placeholder="P0700 / P0730" className={inp} /></Field>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            <H>Step 2 · 维修类型 + 紧急程度</H>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <button key={t.value} onClick={() => set("typeCard", t.value)} className={`rounded-lg border-2 p-3 text-left ${f.typeCard === t.value ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                  <div className="text-[14px] font-semibold">{t.icon} {t.label}</div>
                  <div className="text-[11px] text-slate-500">{t.sub}</div>
                </button>
              ))}
            </div>
            <div className="text-[13px] font-medium text-slate-700">紧急程度</div>
            <div className="grid grid-cols-3 gap-2">
              {URGENCY.map((u) => (
                <button key={u.value} onClick={() => set("urgency", u.value)} className={`rounded-lg border-2 p-3 text-left ${f.urgency === u.value ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                  <div className="text-[13px] font-semibold">{u.icon} {u.label}</div>
                  <div className="text-[11px] text-slate-500">{u.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="flex flex-col gap-4">
            <H>Step 3 · 维修厂（手工输入 · 不建档）</H>
            <Field label="维修厂名 *"><input value={f.shopName} onChange={(e) => set("shopName", e.target.value)} placeholder="Big Sky Transmission" className={inp} /></Field>
            <Field label="地址"><input value={f.shopAddress} onChange={(e) => set("shopAddress", e.target.value)} placeholder="1234 Main St, Bozeman MT" className={inp} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="联系电话 *"><input value={f.shopPhone} onChange={(e) => set("shopPhone", e.target.value)} placeholder="+1-406-555-0188" className={inp} /></Field>
              <Field label="联系人"><input value={f.shopContact} onChange={(e) => set("shopContact", e.target.value)} placeholder="Sam" className={inp} /></Field>
            </div>
            <Field label="保修期"><input value={f.warranty} onChange={(e) => set("warranty", e.target.value)} placeholder="1 年 / 12,000 mi" className={inp} /></Field>
            <label className="flex items-center gap-2 text-[13px] text-slate-700"><input type="checkbox" checked={f.returning} onChange={(e) => set("returning", e.target.checked)} /> 曾合作过</label>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="flex flex-col gap-4">
            <H>Step 4 · 工时 + 配件 + 凭证</H>
            <div className="grid grid-cols-2 gap-4">
              <Field label="预估工时（小时）*"><input value={f.laborHours} onChange={(e) => set("laborHours", e.target.value)} inputMode="decimal" placeholder="32" className={inp} /></Field>
              <Field label="工时单价（USD）"><input value={f.laborRate} onChange={(e) => set("laborRate", e.target.value)} inputMode="decimal" placeholder="120" className={inp} /></Field>
            </div>
            <Field label="配件清单（每行一条）"><textarea value={f.parts} onChange={(e) => set("parts", e.target.value)} rows={2} placeholder="离合器组件 $680&#10;变速箱油 + 滤清器 $120" className={inp} /></Field>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2 text-[13px]">
              <span className="text-slate-500">工时合计</span><span className="font-bold text-slate-800">${(laborTotal / 100).toLocaleString()}</span>
            </div>
            <Field label="总费用（USD）* · 与维修厂确认"><input value={f.totalCost} onChange={(e) => set("totalCost", e.target.value)} inputMode="decimal" placeholder="3800.00" className={inp} /></Field>
            <div className="rounded-lg border-2 border-dashed border-slate-300 px-4 py-5 text-center text-[12px] text-slate-400">📁 上传报价单 + 维修厂资质 · <b>接入中</b>（暂不必传）</div>
          </div>
        )}

        {/* Step 5 */}
        {step === 5 && (
          <div className="flex flex-col gap-3">
            <H>Step 5 · 确认 + 提交</H>
            <div className="rounded-lg bg-slate-50 p-4 text-[13px]">
              <div className="mb-2 font-semibold">📋 工单概览</div>
              <ul className="flex flex-col gap-1 text-slate-700">
                <li>· 车辆：{veh ? `${veh.vehicle_number} ${veh.model_label_cn}` : "—"}</li>
                <li>· 类型：{typeMeta?.label} · {URGENCY.find((u) => u.value === f.urgency)?.icon}{URGENCY.find((u) => u.value === f.urgency)?.label}</li>
                <li>· 故障：{f.faultDesc || "—"}{f.obdCode ? ` · OBD ${f.obdCode}` : ""}</li>
                <li>· 维修厂：{f.shopName || "—"}{f.shopPhone ? ` · ${f.shopPhone}` : ""}</li>
                <li>· 工时：{f.laborHours || 0}h × ${f.laborRate || 0} = ${(laborTotal / 100).toLocaleString()}</li>
                <li>· 总费用：<b>${Number(f.totalCost || 0).toLocaleString()}</b></li>
              </ul>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[12px] text-emerald-800">✅ 提交后：进「待老板审批」队列 · 审批通过 → 进维修 · 完工录实际金额</div>
          </div>
        )}

        {error && <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</div>}
        </div>
      </div>

      {/* 底栏导航 */}
      <footer className="border-t border-slate-200 bg-white px-8 py-3.5">
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-slate-400">Step {step} / 5 · 完成 {Math.round(((step - 1) / 5) * 100)}%</span>
          <div className="flex gap-2">
            {step > 1 && <button onClick={() => setStep((s) => s - 1)} className="rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-medium hover:bg-slate-50">← 上一步</button>}
            {step < 5 ? (
              <button onClick={next} className="rounded-lg bg-blue-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">下一步 →</button>
            ) : (
              <button onClick={submit} disabled={submitting} className="rounded-lg bg-emerald-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700">{submitting ? "提交中…" : "✓ 提交（走老板审批）"}</button>
            )}
          </div>
        </div>
      </footer>
    </div>,
    document.body,
  );
}

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px]";
function H({ children }: { children: React.ReactNode }) {
  return <div className="text-[15px] font-bold text-slate-900">{children}</div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1.5"><span className="text-[13px] font-medium text-slate-700">{label}</span>{children}</label>;
}
